import axios from "axios";
import type { GetMeiliSearchKeyResponse } from "@interfaces";
import { MeiliSearch as MeiliSearchClient } from "meilisearch";
import {
  MEKI_REST_API_URL,
  MEILI_SEARCH_HOST,
  MEILI_SEARCH_DEFAULT_API_KEY,
  ENV,
} from "../../environment";

export const mekiRestApi = axios.create({
  baseURL: MEKI_REST_API_URL,
});

// Module-level state
let client: MeiliSearchClient | null = null;
let currentApiKey: string | null = null;
let isFetchingKey = false;
let keyFetchPromise: Promise<string> | null = null;
let hasTriedDefaultKey = false;
// Track last client key source for observability
let lastKeySource: "default" | "dynamic" | null = null;

/**
 * Fetches a new API key from the backend
 */
const fetchApiKey = async (): Promise<string> => {
  // If already fetching, wait for the existing promise
  if (isFetchingKey && keyFetchPromise) {
    return keyFetchPromise;
  }

  isFetchingKey = true;
  keyFetchPromise = (async () => {
    try {
      const response = await mekiRestApi.get<GetMeiliSearchKeyResponse>("/search/key");

      currentApiKey = response.data.key;

      return currentApiKey;
    } catch (error) {
      console.error("Error fetching MeiliSearch API key:", error);
      throw error;
    } finally {
      isFetchingKey = false;
      keyFetchPromise = null;
    }
  })();

  return keyFetchPromise;
};

/**
 * Invalidates the current client and API key, forcing a refresh on next use
 */
export const invalidateMeiliSearchClient = (): void => {
  client = null;
  currentApiKey = null;
};

/**
 * Gets or creates a MeiliSearch client with a valid API key
 * Uses default API key first if available, then fetches dynamic key if needed
 */
export const getMeiliSearchClient = async (): Promise<MeiliSearchClient> => {
  // Only fetch in browser environment
  if (typeof window === "undefined") {
    throw new Error("MeiliSearch client can only be created in browser environment");
  }

  // If we don't have a client, create one
  if (!client) {
    let apiKey: string;

    // Try default key first (faster, no API call needed)
    if (MEILI_SEARCH_DEFAULT_API_KEY && !hasTriedDefaultKey) {
      apiKey = MEILI_SEARCH_DEFAULT_API_KEY;
      hasTriedDefaultKey = true;
      lastKeySource = "default";
      if (ENV !== "PROD") console.log("Using default MeiliSearch API key");
    } else {
      // Fetch dynamic key from backend
      apiKey = await fetchApiKey();
      lastKeySource = "dynamic";
      if (ENV !== "PROD")
        console.log("Using dynamic MeiliSearch API key from Meki REST API");
    }

    client = new MeiliSearchClient({
      host: MEILI_SEARCH_HOST,
      apiKey: apiKey,
      timeout: 10000,
    });
    currentApiKey = apiKey;
  }

  return client;
};

/**
 * Helper function to wait/sleep for a specified duration
 * @param ms Milliseconds to wait
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Executes a MeiliSearch operation with automatic retry on 403 and 5xx errors
 * @param operation Function that performs the MeiliSearch operation
 * @param retryCount Number of retries attempted (internal use)
 */
export const executeMeiliSearchOperation = async <T>(
  operation: (client: MeiliSearchClient) => Promise<T>,
  retryCount = 0,
): Promise<T> => {
  const MAX_RETRIES = 2;

  try {
    const client = await getMeiliSearchClient();

    return await operation(client);
  } catch (error) {
    // What MeiliSearch's errors, and the fetch failures under them, may carry.
    const failure = error as
      { response?: { status?: number }; code?: string; message?: string } | undefined;

    const status = failure?.response?.status;

    // Check if it's a 403 error or invalid_api_key error
    const is403Error =
      status === 403 ||
      status === 401 ||
      failure?.code === "invalid_api_key" ||
      failure?.message?.includes("403") ||
      failure?.message?.includes("401") ||
      failure?.message?.includes("invalid_api_key");

    // Check if it's a 5xx server error
    const is5xxError = status !== undefined && status >= 500 && status < 600;

    const shouldRetry = (is403Error || is5xxError) && retryCount < MAX_RETRIES;

    if (shouldRetry) {
      if (is403Error) {
        const keySource = lastKeySource ?? (hasTriedDefaultKey ? "default" : "dynamic");

        console.warn(
          `MeiliSearch 403 error detected with ${keySource} key. Refreshing... (attempt ${
            retryCount + 1
          }/${MAX_RETRIES})`,
        );

        // Invalidate the current client and key for auth errors
        invalidateMeiliSearchClient();

        // On first retry after a default-key failure, keep skipping default
        if (retryCount === 0 && MEILI_SEARCH_DEFAULT_API_KEY) {
          hasTriedDefaultKey = true; // Skip default key on next attempt
        }

        // Wait before retrying, but skip delay on first retry (switching from default to dynamic key)
        if (retryCount > 0) {
          const delayMs = 1000 * retryCount; // Exponential backoff: 1s for 2nd retry, 2s for 3rd, etc.

          console.log(`Waiting ${delayMs}ms before retry...`);
          await sleep(delayMs);
        }
      } else if (is5xxError) {
        const delayMs = 1000 * (retryCount + 1); // Exponential backoff: 1s, 2s, 3s

        console.warn(
          `MeiliSearch server error (${status}). Waiting ${delayMs}ms before retry... (attempt ${
            retryCount + 1
          }/${MAX_RETRIES})`,
        );
        await sleep(delayMs);
      }

      // Retry the operation
      return executeMeiliSearchOperation(operation, retryCount + 1);
    }

    // If not a retryable error or max retries reached, throw the error
    throw error;
  }
};
